import { Collapse, Typography, useTheme } from "@mui/material";
import { Grid } from "@mui/material";
import { FileCopy, Cached } from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useStore } from "utils/useStore";
import { AuthStore } from "stores/AuthStore";
import { AcxGradientTypography } from "components/UI/AcxGradientTypography";
import { AcxGradientBorderContainer } from "../../../UI/AcxGradientBorderContainer";
import Markdown from "markdown-to-jsx";
import AcxChip from "components/UI/AcxChip";
import ChartCardStore from "./ChartCardStore";

export const SpotlightSparkle = () => {
    return (
        <svg
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7.5322 4.25847L6.03835 5.00463C5.58912 5.2277 5.22758 5.59232 5.00297 6.04001L4.25681 7.53232C4.15066 7.74463 3.84604 7.74463 3.73989 7.53232L2.99373 6.04001C2.77066 5.59232 2.40604 5.22924 1.95835 5.00463L0.46758 4.25847C0.255273 4.15232 0.255273 3.8477 0.46758 3.74155L1.96143 2.99539C2.41066 2.77232 2.7722 2.4077 2.99681 1.96001L3.74143 0.467702C3.84758 0.255395 4.1522 0.255395 4.25835 0.467702L5.0045 1.96001C5.22758 2.4077 5.5922 2.77078 6.03989 2.99539L7.53373 3.74155C7.74604 3.8477 7.74604 4.15232 7.53373 4.25847H7.5322Z"
                fill="url(#paint0_linear_25211_23597)"
            />
            <defs>
                <linearGradient
                    id="paint0_linear_25211_23597"
                    x1="0.527385"
                    y1="0.600361"
                    x2="8.14721"
                    y2="7.2488"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#32D1B7" />
                    <stop offset="0.695" stopColor="#349BC7" />
                    <stop offset="1.0001" stopColor="#3664D6" />
                </linearGradient>
            </defs>
        </svg>
    );
};

const Spotlight = observer(({ store }: { store: ChartCardStore }) => {
    const theme = useTheme();
    const authStore = useStore(AuthStore);

    const aiSummaryStore = store.aiSummaryStore;

    const [isSpotlightExpanded, setIsSpotlightExpanded] = useState(false);

    if (!authStore.canUserView("AI Summaries") || !aiSummaryStore) return null;

    const isSpotlightLoading = aiSummaryStore.isSummaryLoading;

    useEffect(() => {
        if (aiSummaryStore.isOutdated) {
            setIsSpotlightExpanded(false);
        }
    }, [aiSummaryStore.isOutdated]);

    if (!store.isDisplayingRenderableChartData) return null;

    const refreshButtonDisabled = !aiSummaryStore.isRefreshable;

    const loadingIndicator = (
        <Grid
            container
            item
            direction="column"
            alignItems="center"
            gap={1}
            xs={12}
        >
            <Grid item>
                <AcxLoadingIndicator
                    alternate="PuffLoader"
                    size={32}
                    style={{
                        zIndex: theme.zIndex.appBar - 2,
                    }}
                />
            </Grid>
            <Grid item>
                <Typography>Generating KBo insights</Typography>
            </Grid>
        </Grid>
    );

    const summaryComponent = (
        <Grid item container xs={12}>
            <Markdown>
                {aiSummaryStore.summary ?? "No insights generated"}
            </Markdown>
        </Grid>
    );

    const collapseComponent = (
        <Collapse
            in={isSpotlightExpanded}
            unmountOnExit
            style={{ width: "100%" }}
        >
            {isSpotlightLoading ? loadingIndicator : summaryComponent}
        </Collapse>
    );

    const headerComponent = (
        <Grid
            container
            item
            direction="row"
            gap={2}
            alignItems="center"
            justifyContent={"space-between"}
            xs={12}
        >
            <Grid
                flexGrow={1}
                onClick={() => {
                    if (
                        !isSpotlightExpanded &&
                        (!aiSummaryStore.summary || aiSummaryStore.isOutdated)
                    ) {
                        store.refreshAISummary();
                    }
                    setIsSpotlightExpanded((expanded) => !expanded);
                }}
                sx={{
                    "&:hover": {
                        cursor: "pointer",
                    },
                }}
            >
                <Grid
                    container
                    direction={"row"}
                    alignItems={"center"}
                    gap={theme.spacing(0.5)}
                >
                    <AcxGradientTypography
                        sx={{
                            color: theme.palette.secondary.dark,
                            textTransform: "uppercase",
                            letterSpacing: "2px",
                        }}
                    >
                        Analyze with kbo
                    </AcxGradientTypography>
                    <SpotlightSparkle />
                </Grid>
                {aiSummaryStore.isOutdated && isSpotlightExpanded && (
                    <AcxChip
                        size="small"
                        color="gray"
                        label="Current filters not reflected"
                    />
                )}
            </Grid>
            <Grid container item direction="row" gap={1} width="fit-content">
                {isSpotlightExpanded && (
                    <>
                        <Grid item>
                            <AcxButton
                                id={`${store.id}-Spotlight-Copy`}
                                fullWidth
                                variant="text"
                                startIcon={<FileCopy />}
                                style={{
                                    backgroundColor: "transparent",
                                    color: theme.palette.gray[400],
                                }}
                                onClick={() => {
                                    aiSummaryStore.copySummaryToClipboard();
                                }}
                            >
                                Copy
                            </AcxButton>
                        </Grid>
                        <Grid item>
                            <AcxButton
                                id={`${store.id}-Spotlight-Refresh`}
                                fullWidth
                                variant="text"
                                startIcon={<Cached />}
                                style={{
                                    backgroundColor: "transparent",
                                    color: refreshButtonDisabled
                                        ? theme.palette.gray[300]
                                        : theme.palette.gray[400],
                                }}
                                disabled={refreshButtonDisabled}
                                onClick={() => {
                                    store.refreshAISummary();
                                    setIsSpotlightExpanded(true);
                                }}
                            >
                                Refresh
                            </AcxButton>
                        </Grid>
                    </>
                )}
            </Grid>
        </Grid>
    );

    if (store.isPresentationMode && !isSpotlightExpanded) {
        return null;
    }

    // TODO: Spotlights don't quite support grouping, so for now grouping just doesn't return a spotlight
    if (store.groupingStore.isGrouped) {
        return null;
    }

    return (
        <AcxGradientBorderContainer>
            <Grid container item gap={1}>
                {headerComponent}
                {collapseComponent}
            </Grid>
        </AcxGradientBorderContainer>
    );
});

export default Spotlight;
